import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, User, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const DemoChat = ({ onComplete, variant = 'default', currentLang }) => {
  const { t } = useTranslation();
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const messagesEndRef = useRef(null);

  // Generate translated demo messages based on current language
  const DEMO_MESSAGES = useMemo(() => [
    {
      id: 1,
      actor: 'ai',
      content: t('demo.aiMessage1'),
      delay: 500
    },
    {
      id: 2,
      actor: 'user',
      content: t('demo.userMessage1'),
      delay: 2000
    },
    {
      id: 3,
      actor: 'ai',
      content: t('demo.aiMessage2'),
      delay: 3500
    },
    {
      id: 4,
      actor: 'user',
      content: t('demo.userMessage2'),
      delay: 5500
    },
    {
      id: 5,
      actor: 'ai',
      content: t('demo.aiMessage3'),
      delay: 7000,
      isFinal: true
    }
  ], [t, currentLang]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Reset and restart demo when language changes
  useEffect(() => {
    setVisibleMessages([]);
    setShowCTA(false);
    setIsTyping(false);
    
    let timeouts = [];
    let isMounted = true;
    
    DEMO_MESSAGES.forEach((msg, index) => {
      // Show typing indicator before AI messages
      if (msg.actor === 'ai' && index > 0) {
        const typingTimeout = setTimeout(() => {
          if (isMounted) setIsTyping(true);
        }, msg.delay - 1000);
        timeouts.push(typingTimeout);
      }

      const messageTimeout = setTimeout(() => {
        if (isMounted) {
          setIsTyping(false);
          setVisibleMessages(prev => {
            // Prevent duplicates
            if (prev.find(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          
          if (msg.isFinal) {
            setTimeout(() => {
              if (isMounted) setShowCTA(true);
            }, 500);
          }
        }
      }, msg.delay);
      timeouts.push(messageTimeout);
    });

    return () => {
      isMounted = false;
      timeouts.forEach(t => clearTimeout(t));
    };
  }, [DEMO_MESSAGES, currentLang]);

  useEffect(() => {
    scrollToBottom();
  }, [visibleMessages, isTyping]);

  const bgClass = variant === 'mobile' ? 'bg-white' : 'bg-slate-50';

  return (
    <div className={`flex flex-col h-full ${bgClass}`}>
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence mode="popLayout">
          {visibleMessages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex gap-3 ${msg.actor === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.actor === 'ai' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ocean-blue to-sky-blue flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.actor === 'user'
                    ? 'bg-ocean-blue text-white rounded-br-md'
                    : 'bg-white border border-slate-200 text-slate-700 rounded-bl-md shadow-sm'
                }`}
              >
                <p className="text-sm whitespace-pre-line leading-relaxed">{msg.content}</p>
              </div>
              {msg.actor === 'user' && (
                <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </motion.div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ocean-blue to-sky-blue flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* CTA Section */}
      <AnimatePresence>
        {showCTA && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 border-t border-slate-200 bg-white"
          >
            {variant === 'mobile' ? (
              <div className="space-y-3">
                <Button
                  onClick={() => onComplete?.('signup')}
                  className="w-full rounded-full bg-coral hover:bg-coral/90 text-white"
                  size="lg"
                  data-testid="demo-continue-free-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('demo.continueFree')}
                </Button>
                <Button
                  onClick={() => onComplete?.('home')}
                  variant="outline"
                  className="w-full rounded-full"
                  size="lg"
                  data-testid="demo-back-home-btn"
                >
                  {t('demo.backToHome')}
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => onComplete?.('signup')}
                className="w-full rounded-full bg-coral hover:bg-coral/90 text-white"
                size="lg"
                data-testid="demo-start-learning-btn"
              >
                {t('demo.startLearningFree')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DemoChat;
